import { JSX, Show, For, createSignal } from "solid-js";
import { useBrandl2WebstoreTelescopeContext } from "../context/brandl2_webstore_telescope_context";
import { VoucherHighlight } from "~/server/types/brand";
import {
  cannotBeClubbedWithOffers,
  cardCanBeClubbed,
  cardsCannotBeClubbed,
  clubWithOffers,
  multipleTimes,
  offline,
  online,
  onlineOffline,
  singleTime,
  subscription,
  validForOneYear,
} from "~/assets/brand_highlight_svg_icons";

const fallbackDescription =
  "Your prepaid ticket to a world of purchases, easily redeemable across eligible product categories. Explore millions of products and find the perfect gift for any occasion with gift cards.";
/**
 * About the Gift Card section with description and highlight grid
 * Matches Figma design: 606-31535
 */
export function AboutTheGiftCard(): JSX.Element {
  const { routeData } = useBrandl2WebstoreTelescopeContext();

  const [showFullDescription, setShowFullDescription] = createSignal(false);

  const brandName = () => routeData().brand.name;
  const description = () =>
    routeData().brandExtraMetadata.voucherDescription || fallbackDescription;
  const highlights = () =>
    routeData().brandExtraMetadata.voucherHighlights || [];

  const shortDescription = () => {
    const desc = description();
    return desc.length > 200 && !showFullDescription()
      ? desc.substring(0, 200) + "..."
      : desc;
  };

  return (
    <div class="flex w-full flex-col gap-6">
      {/* Header with title and description */}
      <div class="flex w-full flex-col gap-2">
        <h2 class="text-title-4-semi-bold text-text-normal-primary">
          About {brandName()} gift card
        </h2>
        <Show when={description()}>
          <div class="flex flex-col gap-3">
            <p class="text-para-3-regular text-text-normal-secondary">
              {shortDescription()}
            </p>
            <Show when={description().length > 200}>
              <button
                onClick={() => setShowFullDescription(!showFullDescription())}
                class="text-brand-base w-fit text-para-3-medium hover:underline"
              >
                {showFullDescription() ? "Show less" : "Read more"}
              </button>
            </Show>
          </div>
        </Show>
      </div>

      {/* Highlights Grid - 2x2 layout */}
      <Show when={highlights().length > 0}>
        <div class="grid grid-cols-2 gap-4">
          <For each={highlights()}>
            {(highlight) => <HighlightItem highlight={highlight} />}
          </For>
        </div>
      </Show>
    </div>
  );
}

type HighlightItemProps = {
  highlight: VoucherHighlight;
};

function HighlightItem(props: HighlightItemProps): JSX.Element {
  const config = getHighlightConfig(props.highlight);

  return (
    <div class="flex items-start gap-2 stroke-text-normal-primary">
      {/* Icon */}
      <div class="shrink-0">{config.iconBuilder()}</div>

      {/* Content */}
      <div class="flex flex-1 flex-col gap-0.5">
        <p class="text-para-3-semi-bold text-text-normal-primary">
          {config.title}
        </p>
        <p class="text-label-regular text-text-normal-secondary">
          {config.subtitle}
        </p>
      </div>
    </div>
  );
}

type HighlightConfig = {
  iconBuilder: () => JSX.Element;
  title: string;
  subtitle: string;
};

function getHighlightConfig(highlight: VoucherHighlight): HighlightConfig {
  switch (highlight) {
    case "VALID_FOR_ONE_YEAR":
      return {
        iconBuilder: validForOneYear,
        title: "Valid for 1 year",
        subtitle: "After purchase",
      };
    case "VALID_FOR_NINE_MONTHS":
      return {
        iconBuilder: validForOneYear,
        title: "Valid for 9 months",
        subtitle: "After purchase",
      };
    case "VALID_FOR_SIX_MONTHS":
      return {
        iconBuilder: validForOneYear,
        title: "Valid for 6 months",
        subtitle: "After purchase",
      };
    case "VALID_FOR_THREE_MONTHS":
      return {
        iconBuilder: validForOneYear,
        title: "Valid for 3 months",
        subtitle: "After purchase",
      };
    case "VALIDITY":
      return {
        iconBuilder: validForOneYear,
        title: "Check validity",
        subtitle: "On the brand website",
      };
    case "CLUB_WITH_OFFERS":
      return {
        iconBuilder: clubWithOffers,
        title: "Club offers",
        subtitle: "Works on top of brand offers",
      };
    case "CARDS_CAN_BE_CLUBBED":
      return {
        iconBuilder: cardCanBeClubbed,
        title: "Combine cards",
        subtitle: "Multiple cards can be used",
      };
    case "CARDS_CANT_BE_CLUBBED":
      return {
        iconBuilder: cardsCannotBeClubbed,
        title: "Single use",
        subtitle: "One card at a time",
      };
    case "CANT_BE_CLUBBED_WITH_OFFERS":
      return {
        iconBuilder: cannotBeClubbedWithOffers,
        title: "Exclusive offer",
        subtitle: "Not valid with other offers",
      };
    case "SINGLE_TIME":
      return {
        iconBuilder: singleTime,
        title: "Single use",
        subtitle: "Can be used once",
      };
    case "MULTIPLE_TIMES":
      return {
        iconBuilder: multipleTimes,
        title: "Multiple uses",
        subtitle: "Can be used multiple times",
      };
    case "SUBSCRIPTION":
      return {
        iconBuilder: subscription,
        title: "Subscription",
        subtitle: "Recurring validity",
      };
    case "ONLINE":
      return {
        iconBuilder: online,
        title: "Online only",
        subtitle: "Use on website or app",
      };
    case "OFFLINE":
      return {
        iconBuilder: offline,
        title: "In-store only",
        subtitle: "Use at physical stores",
      };
    case "ONLINE_OFFLINE":
      return {
        iconBuilder: onlineOffline,
        title: "Online & in-store",
        subtitle: "Use anywhere",
      };
    default:
      return {
        iconBuilder: () => <></>,
        title: highlight,
        subtitle: "",
      };
  }
}
